import {
  get,
  forEach,
  noop,
  isFunction,
} from 'lodash';
import React from 'react';
import { shape, node, object, func } from 'prop-types';
import {
  DefaultBrowserBehavior,
  DefaultDeviceController,
  DefaultMeetingSession,
  MeetingSessionConfiguration,
  LogLevel
} from 'amazon-chime-sdk-js';
import { stringify } from 'telejson';

import siteConfig from '~/config';
import { EMPTY_OBJ, EMPTY_ARR } from 'Services/constants/empty';
import meetingConstants from 'Services/constants/meetings';
import getDisplayName from 'Services/getDisplayName';
import telehealthRequest from 'Services/telehealthRequest';
import isEmpty from 'Services/helpers/isEmpty';
import MeetingSessionPOSTLogger from 'Services/chimeLogging';
import storage from 'Services/storage';
import UAParser from 'ua-parser-js';
import getBrowserDetails from '../../Services/helpers/getBrowserDetails';

const nullIfEmpty = (value) => (isEmpty(value) ? null : value);

export const MeetingContextMeetingControlsProps = shape({
  start: func.isRequired,
  join: func.isRequired,
  stop: func.isRequired,
  submitFeedback: func.isRequired,
  dismissFeedback: func.isRequired,
  getHasSubmittedFeedback: func.isRequired,
  startNewLogger: func.isRequired,
});

export const MeetingContextDevicePermissionsProps = shape({
  check: func.isRequired,
  prompt: func.isRequired,
  getDevicePermissions: func.isRequired,
});

export const MeetingContextDeviceControlsProps = shape({
  promptForAvailableDevices: func.isRequired,
  setSelectedAudioInDeviceId: func.isRequired,
  setSelectedVideoDeviceId: func.isRequired,
  setSelectedAudioOutDeviceId: func.isRequired,
  getAvailableDevices: func.isRequired,
  getSelectedDeviceIds: func.isRequired,
  videoPreviewStart: func.isRequired,
});

export const MeetingContextAVControlsProps = shape({
  getState: func.isRequired,
  audioIn: shape({
    on: func.isRequired,
    off: func.isRequired,
    analyserNode: shape({
      create: func.isRequired,
      destroy: func.isRequired,
    }),
  }),
  video: shape({
    on: func.isRequired,
    off: func.isRequired,
  }),
  audioOut: shape({
    on: func.isRequired,
    off: func.isRequired,
  }),
});

export const MeetingAVInputStates = {
  DISABLED: 'disabled',
  ON: 'on',
  OFF: 'off',
};

export const MeetingContext = React.createContext({
  isAudioOutputSelectionSupported: true,
  isSupportedBrowser: true,
  registerMeetingCallbacks: noop,
  deviceControls: EMPTY_OBJ,
  devicePermissions: EMPTY_OBJ,
  meetingControls: EMPTY_OBJ,
  avControls: EMPTY_OBJ,
});

export class MeetingProvider extends React.Component {
  /*
    available callbacks:
    - onPromptForAvailableDevicesStart()
    - onPromptForAvailableDevicesDenied()
    - onPromptForAvailableDevicesEnd()
    - onAudioInDevicePermissionFailed()
    - onVideoDevicePermissionFailed()
    - onAudioOutDevicePermissionFailed()
    - onAvailableAudioInDevicesUpdated(MediaDeviceInfo[])
    - onAvailableAudioOutDevicesUpdated(MediaDeviceInfo[])
    - onAvailableVideoDevicesUpdated(MediaDeviceInfo[])
    - onSelectedAudioInDeviceIdUpdated(deviceId<string>)
    - onSelectedVideoDeviceIdUpdated(deviceId<string>)
    - onSelectedAudioOutDeviceIdUpdated(deviceId<string>)
    - onVideoPreviewStart(deviceId<string>, localVideoElement<HTMLVideoElement>)
    - onVideoPreviewStop(deviceId<string>, localVideoElement<HTMLVideoElement>)

    - onMeetingStart(success<bool>, error?<Error>)
    - onMeetingJoin(joinedWithVideo<bool>, joinedWithAudioIn<bool>, joinedWithAudioOut<bool>)
    - onMeetingEnd()
    - onMeetingAudioVideoDidStart()
    - onMeetingAudioVideoDidStartConnecting()
    - onMeetingAudioVideoDidStop()
    - onMeetingVideoTileUpdated(bindVideoElement<func>, VideoTileState)
    - onMeetingVideoTileWasRemoved(wasRemote<bool>, tileId<number>)
    - onMeetingVideoAvailabilityUpdated()
    - onSubmittedFeedback
    - onVideoQualityUpdated(qualityId<string>)

    - onLocalAudioOutUpdated(isEnabled<bool>)
    - onAttendeeMicrophoneUpdated()
    - onLocalMuteUpdated(isMuted<bool>)
    - onRemoteAttendeePresenceUpdated(chimeAttendeeId<string>, isPresent<bool>)
  */
  constructor(props) {
    super(props);
    const storedDeviceIds = storage.get('devices', EMPTY_OBJ);

    this.browserBehaviour = new DefaultBrowserBehavior();
    this.isSupportedBrowser = this.browserBehaviour.isSupported();
    this.callbacks = EMPTY_OBJ;
    this.meetingConfig = null;
    this.meetingSession = null;
    this.localAudioOutElement = null;
    this.logger = this.createLogger();
    // if (siteConfig.ENVIRONMENT !== 'production') {
    // this.logger.setLogLevel(LogLevel.INFO);
    // }
    this.deviceController = null;
    this.audioInputAnalyserNode = null;
    this.hasSubmittedFeedback = false;
    this.videoQualityId = meetingConstants.qualityDefault;
    this.browserDetails = new UAParser().getResult() || {};

    if (this.isSupportedBrowser) {
      this.deviceController = new DefaultDeviceController(this.logger);
      this.deviceController.addDeviceChangeObserver(this.deviceChangeObserver);
      this.deviceController.setDeviceLabelTrigger(this.deviceLabelTrigger);
      // set video defalt  quality
      const {
        width,
        height,
        frameRate,
        maxBandwidthKbps,
      } = meetingConstants.qualities[this.videoQualityId].params;
      this.deviceController.chooseVideoInputQuality(width, height, frameRate, maxBandwidthKbps);
    } else {
      props.analyticsContext.trackEvent('[MEETING CONTEXT CALLBACKS] onUnsupportedBrowserDetected');
    }

    let testAudioElement = document.createElement('audio');
    this.isAudioOutputSelectionSupported = isFunction(testAudioElement.setSinkId);
    if (!this.isAudioOutputSelectionSupported) {
      props.analyticsContext.trackEvent('[MEETING CONTEXT CALLBACKS] onAudioOutputSelectionUnsupported');
    }
    testAudioElement = false;

    this.availableDevices = {
      audioIn: EMPTY_ARR,
      video: EMPTY_ARR,
      audioOut: EMPTY_ARR,
    };
    this.selectedDeviceIds = {
      audioIn: nullIfEmpty(storedDeviceIds.audioIn),
      video: nullIfEmpty(storedDeviceIds.video),
      audioOut: nullIfEmpty(storedDeviceIds.audioOut),
    };
    this.hasNoAVInputsSet = (
      !this.selectedDeviceIds.audioIn &&
      !this.selectedDeviceIds.video &&
      this.isSupportedBrowser
    );
    this.permissions = {
      camera: 'prompt',
      microphone: 'prompt',
    };

    this.providerValue = {
      isSupportedBrowser: this.isSupportedBrowser,
      isAudioOutputSelectionSupported: this.isAudioOutputSelectionSupported,
      registerMeetingCallbacks: this.registerMeetingCallbacks,
      deviceControls: this.deviceControls,
      devicePermissions: this.devicePermissions,
      meetingControls: this.meetingControls,
      avControls: this.avControls,
    };
  }

  createLogger = () => (
    new MeetingSessionPOSTLogger(
      'ChimeMeetingLogs',
      this.getMeetingConfig,
      85,
      10 * 1000,
      siteConfig.POST_LOGGER_URL,
      LogLevel.INFO,
      LogLevel.INFO,
    )
  )

  getMeetingConfig = () => this.meetingConfig || {};

  /*
    BEGIN CONTEXT HELPERS
  */
  executeCallback = (key, ...args) => {
    const {
      analyticsContext,
    } = this.props;
    forEach(this.callbacks, (callbackObject) => {
      get(callbackObject, [key], noop)(...args);
    });
    this.logger.info(['[MEETING CONTEXT CALLBACKS]', key, stringify([...args])].join('\t'));
    analyticsContext.trackEvent(['[MEETING CONTEXT CALLBACKS]', key, stringify([...args])].join('\t'));
  }
  registerMeetingCallbacks = (id, callbackObject = EMPTY_OBJ) => {
    if (this.callbacks[id]) {
      throw new Error(`[meetingContext] A callback set already exists for this id: ${id}`);
    }
    this.callbacks = {
      ...this.callbacks,
      [id]: callbackObject,
    };
    this.logger.info(['[MEETING CONTEXT CALLBACKS]', 'registration', id, Object.keys(callbackObject)].join('\t'));
    return () => this.unregisterMeetingCallbacks(id);
  }
  unregisterMeetingCallbacks = (id) => {
    this.logger.info(['[MEETING CONTEXT CALLBACKS]', 'deregistration', id].join('\t'));
    delete this.callbacks[id];
  }
  /*
    END CONTEXT HELPERS
  */

  deviceChangeObserver = {
    audioInputsChanged: (audioIn) => {
      this.availableDevices.audioIn = audioIn;
      this.executeCallback('onAvailableAudioInDevicesUpdated', audioIn);
    },
    audioOutputsChanged: (audioOut) => {
      this.availableDevices.audioOut = audioOut;
      this.executeCallback('onAvailableAudioOutDevicesUpdated', audioOut);
    },
    videoInputsChanged: (video) => {
      this.availableDevices.video = video;
      this.executeCallback('onAvailableVideoDevicesUpdated', video);
    },
  };

  audioVideoObserver = {
    audioVideoDidStart: () => {
      this.executeCallback('onMeetingAudioVideoDidStart');
    },
    audioVideoDidStartConnecting: (isReconnecting) => {
      this.executeCallback('onMeetingAudioVideoDidStartConnecting', isReconnecting);
    },
    audioVideoDidStop: (...args) => {
      this.executeCallback('onMeetingAudioVideoDidStop', ...args);
    },
    videoTileDidUpdate: (tileState) => {
      if (!this.meetingSession) return;
      this.executeCallback(
        'onMeetingVideoTileUpdated',
        this.meetingSession.audioVideo.bindVideoElement.bind(this.meetingSession.audioVideo),
        tileState
      );
    },
    videoTileWasRemoved: (tileId) => {
      if (!this.meetingSession) return;
      const localTile = [...this.meetingSession.audioVideo.videoTileController.tileMap.values()]
        .find((tile) => tile.state.localTile);
      if (localTile) {
        this.executeCallback('onMeetingVideoTileWasRemoved', localTile.tileId !== tileId, tileId);
      }
    },
    videoAvailabilityDidChange: (videoAvailability) => {
      this.executeCallback('onMeetingVideoAvailabilityUpdated', videoAvailability);
    },
    // stream health callbacks
    connectionDidBecomePoor: (...args) => {
      this.executeCallback('onConnectionDidBecomePoor', ...args);
    },
    connectionDidSuggestStopVideo: (...args) => {
      this.executeCallback('onConnectionDidSuggestStopVideo', ...args);
    },
    videoNotReceivingEnoughData: (...args) => {
      this.executeCallback('onVideoNotReceivingEnoughData', ...args);
    },
    // connectionHealthDidChange: (...args) => {
    //   this.executeCallback('onConnectionHealthDidChange', ...args);
    // },
    estimatedDownlinkBandwidthLessThanRequired: (...args) => {
      this.executeCallback('onEstimatedDownlinkBandwidthLessThanRequired', ...args);
    },
  };

  attendeePresenceObserver = (chimeAttendeeId, isPresent) => {
    // an attendee that is not yourself
    if (this.meetingConfig.credentials.attendeeId !== chimeAttendeeId) {
      if (isPresent) {
        this.meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(chimeAttendeeId, this.attendeeVolumeObserver);
      } else {
        this.meetingSession.audioVideo.realtimeUnsubscribeFromVolumeIndicator(chimeAttendeeId);
      }
      this.executeCallback('onRemoteAttendeePresenceUpdated', chimeAttendeeId, isPresent);
    }
  }

  attendeeVolumeObserver = (chimeAttendeeId, volumeLevel, isMuted) => {
    if (isMuted === null) return;
    this.executeCallback('onAttendeeMicrophoneUpdated', chimeAttendeeId, isMuted);
  }

  localMutedObserver = (...args) => {
    this.executeCallback('onLocalMuteUpdated', ...args);
  }

  deviceLabelTrigger = async () => {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    } catch (e) {
      this.executeCallback('onPromptForAvailableDevicesDenied');
      throw e;
    }
  }

  devicePermissions = {
    check: async (permissionName) => {
      try {
        await navigator.permissions.query({ name: permissionName })
          .then((permission) => {
            this.permissions[permissionName] = permission.state;
            this.executeCallback('onDevicePermissionChange', permissionName, permission.state);
            permission.addEventListener('change', () => {
              this.permissions[permissionName] = permission.state;
              this.executeCallback('onDevicePermissionChange', permissionName, permission.state);
            });
          });
      } catch (e) {
        // firefox will fail at the above query, so prompt immediately
        this.devicePermissions.prompt(permissionName);
      }
    },
    prompt: async (permissionName) => {
      try {
        if (permissionName === 'camera') {
          await navigator.mediaDevices.getUserMedia({ video: true }, noop, noop);
          this.permissions[permissionName] = 'granted';
          this.executeCallback('onDevicePermissionChange', permissionName, 'granted');
        } else if (permissionName === 'microphone') {
          await navigator.mediaDevices.getUserMedia({ audio: true }, noop, noop);
          this.permissions[permissionName] = 'granted';
          this.executeCallback('onDevicePermissionChange', permissionName, 'granted');
        }
      } catch (e) {
        // firefox throws a NotAllowedError if permission is denied
        // safari throws a TypeError with 'denied permission' in its message
        if (e.message.indexOf('denied permission') >= 0 || e.name === 'NotAllowedError') {
          this.permissions[permissionName] = 'denied';
          this.executeCallback('onDevicePermissionChange', permissionName, 'denied');
        } else {
          window.console.log('[meetingContext] prompt for permissions error', e);
        }
      }
    },
    getDevicePermissions: () => this.permissions,
  }

  deviceControls = {
    promptForAvailableDevices: async () => {
      this.executeCallback('onPromptForAvailableDevicesStart');
      if (this.deviceController) {
        // await fetching all access promises
        await this.deviceController.listAudioInputDevices()
          .then(this.deviceChangeObserver.audioInputsChanged);
        await this.deviceController.listVideoInputDevices()
          .then(this.deviceChangeObserver.videoInputsChanged);
        await this.deviceController.listAudioOutputDevices()
          .then(this.deviceChangeObserver.audioOutputsChanged);
      }
      this.executeCallback('onPromptForAvailableDevicesEnd', this.availableDevices);
    },
    setSelectedAudioInDeviceId: async (audioInDeviceId) => {
      const audioIn = nullIfEmpty(audioInDeviceId);
      // only fire callbacks if the device has actually changed
      if (audioIn !== this.selectedDeviceIds.audioIn) {
        this.selectedDeviceIds.audioIn = audioIn;
        if (this.meetingSession) {
          // update the meetings AV
          await this.meetingSession.audioVideo.chooseAudioInputDevice(audioIn);
        } else {
          await this.deviceController.chooseAudioInputDevice(audioIn);
        }
        await storage.set('devices', this.selectedDeviceIds);
      }
      this.executeCallback('onSelectedAudioInDeviceIdUpdated', audioIn);
    },
    setSelectedVideoDeviceId: async (videoDeviceId) => {
      const video = nullIfEmpty(videoDeviceId);
      if (video !== this.selectedDeviceIds.video) {
        this.selectedDeviceIds.video = video;
        if (this.meetingSession) {
          // update the meetings AV
          await this.meetingSession.audioVideo.chooseVideoInputDevice(video);
          this.meetingSession.audioVideo.startLocalVideoTile();
        } else {
          await this.deviceController.chooseVideoInputDevice(video);
        }
        await storage.set('devices', this.selectedDeviceIds);
      }
      this.executeCallback('onSelectedVideoDeviceIdUpdated', video);
    },
    setSelectedAudioOutDeviceId: async (audioOutDeviceId) => {
      const audioOut = nullIfEmpty(audioOutDeviceId);
      if (audioOut !== this.selectedDeviceIds.audioOut) {
        this.selectedDeviceIds.audioOut = audioOut;
        if (this.meetingSession) {
          // update the meetings AV
          await this.meetingSession.audioVideo.chooseAudioOutputDevice(audioOut);
        } else {
          await this.deviceController.chooseAudioOutputDevice(audioOut);
        }
        this.executeCallback('onSelectedAudioOutDeviceIdUpdated', audioOut);
        await storage.set('devices', this.selectedDeviceIds);
      }
    },
    videoPreviewStart: async (localVideoElement) => {
      if (!localVideoElement) throw new Error('[meetingContext] no local video element found');
      await this.deviceController.chooseVideoInputDevice(this.selectedDeviceIds.video);
      this.deviceController.startVideoPreviewForVideoInput(localVideoElement);
      this.executeCallback('onVideoPreviewStart', this.selectedDeviceIds.video, localVideoElement);
      return async () => {
        this.deviceController.stopVideoPreviewForVideoInput(localVideoElement);
        if (this.meetingSession) {
          await this.avControls.video.on();
        } else {
          await this.deviceController.chooseVideoInputDevice(null);
        }
        this.executeCallback('onVideoPreviewStop', this.selectedDeviceIds.video, localVideoElement);
      };
    },
    getAvailableDevices: () => this.availableDevices,
    getSelectedDeviceIds: () => this.selectedDeviceIds,
  }

  meetingControls = {
    start: async (meeting, attendee, identityEmail) => {
      const {
        analyticsContext,
      } = this.props;
      if (!meeting) throw new Error('[meetingContext] A meeting is required');
      if (!attendee) throw new Error('[meetingContext] An attendee is required');
      if (!this.deviceController) throw new Error('[meetingContext] A device controller is required');
      await this.deviceControls.promptForAvailableDevices();
      const supportedVideoCodecs = await this.browserBehaviour.supportedVideoCodecs();
      analyticsContext.trackEvent([
        '[MEETING CONTEXT] supported video codecs',
        ...supportedVideoCodecs,
      ].join('\t'));
      try {
        this.meetingConfig = new MeetingSessionConfiguration(meeting, attendee);
        this.logger.info([
          '[MEETING CONTEXT] chimeMeetingId',
          meeting.MeetingId,
          'chimeAttendeeId',
          attendee.AttendeeId,
          'externalUserId',
          attendee.ExternalUserId,
          'identityEmail',
          identityEmail,
        ].join('\t'));

        const bd = getBrowserDetails(this.browserDetails || {});
        this.logger.info([
          '[MEETING CONTEXT] attendee joined meeting',
          `${meeting.MeetingId}:${attendee.ExternalUserId} ${bd.browser}:${bd.device}:${bd.os}`,
        ].join('\t'));
        this.meetingSession = new DefaultMeetingSession(this.meetingConfig, this.logger, this.deviceController);
        this.meetingSession.audioVideo.realtimeSubscribeToMuteAndUnmuteLocalAudio(this.localMutedObserver);
        this.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(this.attendeePresenceObserver);
        this.meetingSession.audioVideo.addObserver(this.audioVideoObserver);
        this.meetingSession.audioVideo.start();
        analyticsContext.trackEvent([
          '[MEETING CONTEXT] chimeMeetingId',
          meeting.MeetingId,
          'chimeAttendeeId',
          attendee.AttendeeId,
        ].join('\t'));
        this.hasSubmittedFeedback = false;
        this.executeCallback('onMeetingStart', true);
      } catch (e) {
        this.executeCallback('onMeetingStart', false, e.message);
      }
    },
    join: async (localAudioOutElement) => {
      if (!localAudioOutElement) throw new Error('[meetingContext] no local audioOut element found');
      if (!this.meetingSession) throw new Error('[meetingContext] no current meeting session');
      this.localAudioOutElement = localAudioOutElement;
      const hasVideoInputDeviceId = !isEmpty(this.selectedDeviceIds.video);
      const hasAudioInputDeviceId = !isEmpty(this.selectedDeviceIds.audioIn);

      if (hasVideoInputDeviceId) {
        // we only ever join after showing a preview, which automatically starts our video, so dont call this again
        // this is causing the video to stop as the chime-sdk doesnt handle the race condition of them both happening
        // at the same time
        // await this.meetingSession.audioVideo.chooseVideoInputDevice(this.selectedDeviceIds.video);
        this.meetingSession.audioVideo.startLocalVideoTile();
      }

      if (hasAudioInputDeviceId) {
        await this.meetingSession.audioVideo.chooseAudioInputDevice(this.selectedDeviceIds.audioIn);
        this.meetingSession.audioVideo.realtimeUnmuteLocalAudio();
      }

      await this.meetingSession.audioVideo.chooseAudioOutputDevice(this.selectedDeviceIds.audioOut);
      // no need to bind and unbind this mid meeting, so immediately connect it
      const hasAudioOutput = this.meetingSession.audioVideo.bindAudioElement(localAudioOutElement);
      this.executeCallback('onMeetingJoin', hasVideoInputDeviceId, hasAudioInputDeviceId, hasAudioOutput);
    },
    stop: () => {
      if (!this.meetingSession) return;
      this.meetingSession.audioVideo.stop();
      this.meetingSession.audioVideo.realtimeUnsubscribeToMuteAndUnmuteLocalAudio(this.localMutedObserver);
      this.meetingSession.audioVideo.realtimeUnsubscribeToAttendeeIdPresence(this.attendeePresenceObserver);
      this.meetingSession.audioVideo.removeObserver(this.audioVideoObserver);
      this.meetingSession = null;
      this.executeCallback('onMeetingEnd');
    },
    submitFeedback: (feedbackText, isPositive) => {
      if (!this.hasSubmittedFeedback) {
        const bd = getBrowserDetails(this.browserDetails || {});

        const url = 'event/feedback';
        telehealthRequest({
          url,
          method: 'POST',
          data: {
            meetingId: this.meetingConfig.meetingId,
            attendeeId: this.meetingConfig.credentials.externalUserId,
            feedback: feedbackText,
            isPositiveFb: isPositive.toString(),
            browserDetails: bd.browser,
            deviceDetails: bd.device,
            osDetails: bd.os,
          },
        }).catch((error) => {
          console.error('Could not send feedback', error);
        });

        this.props.analyticsContext.trackEvent(`ATTENDEE_ISSUE_REPORT: ${feedbackText}`);
        this.hasSubmittedFeedback = true;
        this.executeCallback('onSubmittedFeedback');
      }
    },
    dismissFeedback: () => {
      this.hasSubmittedFeedback = true;
    },
    getHasSubmittedFeedback: () => this.hasSubmittedFeedback,
    startNewLogger: () => {
      this.logger = this.createLogger();
    },
  }

  avControls = {
    getState: () => {
      if (!this.meetingSession) throw new Error('[meetingContext] no current meeting session');
      const state = {
        audioIn: MeetingAVInputStates.DISABLED,
        video: MeetingAVInputStates.DISABLED,
        videoQuality: this.videoQualityId,
        audioOut: MeetingAVInputStates.OFF,
      };
      if (this.selectedDeviceIds.audioIn) {
        if (get(this.meetingSession, ['audioVideo', 'realtimeController', 'state', 'muted'])) {
          state.audioIn = MeetingAVInputStates.OFF;
        } else {
          state.audioIn = MeetingAVInputStates.ON;
        }
      }
      if (this.selectedDeviceIds.video) {
        if (
          get(this.meetingSession, ['audioVideo', 'videoTileController', 'currentLocalTile', 'tileState', 'active'])
        ) {
          state.video = MeetingAVInputStates.ON;
        } else {
          state.video = MeetingAVInputStates.OFF;
        }
      }
      if (get(this.meetingSession, ['audioVideo', 'audioMixController', 'audioElement'])) {
        state.audioOut = MeetingAVInputStates.ON;
      }
      return state;
    },
    audioIn: {
      on: () => {
        this.meetingSession.audioVideo.realtimeUnmuteLocalAudio();
      },
      off: () => {
        this.meetingSession.audioVideo.realtimeMuteLocalAudio();
      },
      analyserNode: {
        create: async () => {
          if (!this.selectedDeviceIds.audioIn) {
            throw new Error('[meetingContext] No selected audio in device');
          }
          await this.deviceController.chooseAudioInputDevice(this.selectedDeviceIds.audioIn);
          this.audioInputAnalyserNode = this.deviceController.createAnalyserNodeForAudioInput();
          return this.audioInputAnalyserNode;
        },
        destroy: () => {
          this.audioInputAnalyserNode = null;
        },
      },
    },
    video: {
      on: async () => {
        await this.meetingSession.audioVideo.chooseVideoInputDevice(this.selectedDeviceIds.video);
        this.meetingSession.audioVideo.startLocalVideoTile();
      },
      off: () => {
        this.meetingSession.audioVideo.stopLocalVideoTile();
      },
    },
    videoQuality: {
      set: async (qualityId) => {
        if (!meetingConstants.qualities[qualityId]) {
          throw new Error('[meetingContext] quality not found');
        }
        const {
          width,
          height,
          frameRate,
          maxBandwidthKbps,
        } = meetingConstants.qualities[qualityId].params;
        this.videoQualityId = qualityId;
        await this.meetingSession.audioVideo.chooseVideoInputQuality(width, height, frameRate, maxBandwidthKbps);
        // Call 'chooseVideoInput' device as the changes only take effect the next time a video input device is chosen
        await this.meetingSession.audioVideo.chooseVideoInputDevice(this.selectedDeviceIds.video);
        this.executeCallback('onVideoQualityUpdated', qualityId);
      },
    },
    audioOut: {
      on: async () => {
        await this.meetingSession.audioVideo.bindAudioElement(this.localAudioOutElement);
        this.executeCallback('onLocalAudioOutUpdated', true);
      },
      off: () => {
        this.meetingSession.audioVideo.unbindAudioElement();
        this.executeCallback('onLocalAudioOutUpdated', false);
      },
    },
  };

  render() {
    const {
      children,
    } = this.props;
    return (
      <MeetingContext.Provider value={this.providerValue}>
        {children}
      </MeetingContext.Provider>
    );
  }
}

MeetingProvider.propTypes = {
  children: node.isRequired,
  analyticsContext: object.isRequired,
};

const defaultHOCPropConfig = [
  'isAudioOutputSelectionSupported',
  'isSupportedBrowser',
  'registerMeetingCallbacks',
  'deviceControls',
  'devicePermissions',
  'meetingControls',
  'avControls',
];

export const withMeetingContext = (config = defaultHOCPropConfig) => (WrappedComponent) => {
  const withMeetingContextInner = (props) => (
    <MeetingContext.Consumer>
      {
        (meetingContext) => (
          <WrappedComponent
            {...props}
            {...config.reduce((obj, item) => ({
              ...obj,
              [item]: meetingContext[item],
            }), {})}
          />
        )
      }
    </MeetingContext.Consumer>
  );
  withMeetingContextInner.displayName = `withMeetingContext(${getDisplayName(WrappedComponent)})`;
  return withMeetingContextInner;
};
